import React, { useState, useRef, useEffect, useMemo } from 'react';
import { ChevronDown, Search, X } from 'lucide-react';

/**
 * Searchable single-select dropdown.
 *
 * Props:
 *  - value: current selected value (id)
 *  - onChange: (newValue) => void
 *  - options: [{ value, label, sublabel? }]
 *  - placeholder: text shown when no selection
 *  - disabled: bool
 *  - testId: optional data-testid prefix
 *  - className: extra wrapper classes
 */
export function SearchableSelect({
  value,
  onChange,
  options = [],
  placeholder = 'Select...',
  disabled = false,
  testId = 'searchable-select',
  className = '',
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const wrapRef = useRef(null);
  const inputRef = useRef(null);

  const selected = useMemo(
    () => options.find((o) => String(o.value) === String(value)) || null,
    [options, value]
  );

  const filtered = useMemo(() => {
    if (!search.trim()) return options;
    const q = search.toLowerCase();
    return options.filter((o) => {
      const hay = `${o.label || ''} ${o.sublabel || ''}`.toLowerCase();
      return hay.includes(q);
    });
  }, [options, search]);

  // Close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
        setSearch('');
      }
    };
    if (open) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  // Auto-focus search input on open
  useEffect(() => {
    if (open && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 10);
    }
  }, [open]);

  const handleSelect = (val) => {
    onChange(val);
    setOpen(false);
    setSearch('');
  };

  return (
    <div ref={wrapRef} className={`relative ${className}`} data-testid={testId}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => !disabled && setOpen((o) => !o)}
        className={`w-full px-3 py-2 border border-slate-300 rounded text-sm flex items-center justify-between gap-2 bg-white text-left focus:ring-2 focus:ring-primary focus:border-primary outline-none ${
          disabled ? 'bg-slate-100 cursor-not-allowed text-slate-400' : 'hover:border-slate-400'
        }`}
        data-testid={`${testId}-button`}
      >
        <span className={`truncate ${selected ? 'text-slate-800' : 'text-slate-400'}`}>
          {selected ? selected.label : placeholder}
        </span>
        <div className="flex items-center gap-1 shrink-0">
          {selected && !disabled && (
            <X
              size={14}
              className="text-slate-400 hover:text-slate-700"
              onClick={(e) => {
                e.stopPropagation();
                onChange('');
              }}
              data-testid={`${testId}-clear`}
            />
          )}
          <ChevronDown size={16} className={`text-slate-400 transition-transform ${open ? 'rotate-180' : ''}`} />
        </div>
      </button>

      {open && !disabled && (
        <div
          className="absolute z-50 mt-1 w-full bg-white border border-slate-200 rounded shadow-lg overflow-hidden"
          data-testid={`${testId}-panel`}
        >
          <div className="p-2 border-b bg-slate-50 sticky top-0">
            <div className="relative">
              <Search size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                ref={inputRef}
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search..."
                className="w-full pl-7 pr-2 py-1.5 border border-slate-300 rounded text-sm focus:ring-2 focus:ring-primary focus:border-primary outline-none"
                data-testid={`${testId}-search-input`}
              />
            </div>
          </div>
          <div className="max-h-60 overflow-y-auto">
            {filtered.length === 0 ? (
              <div className="px-3 py-2 text-sm text-slate-400">No results</div>
            ) : (
              filtered.map((o) => {
                const isSel = String(o.value) === String(value);
                return (
                  <button
                    key={o.value}
                    type="button"
                    onClick={() => handleSelect(o.value)}
                    className={`w-full text-left px-3 py-2 text-sm hover:bg-orange-50 flex flex-col ${
                      isSel ? 'bg-orange-50 text-orange-700 font-medium' : 'text-slate-700'
                    }`}
                    data-testid={`${testId}-option-${o.value}`}
                  >
                    <span className="truncate">{o.label}</span>
                    {o.sublabel && <span className="text-xs text-slate-400 truncate">{o.sublabel}</span>}
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default SearchableSelect;
